import React from 'react';
import { DialogTitle } from '@headlessui/react';
import { Modal } from './Modal';
import { ButtonContent } from './ButtonContent';
import { AnimatedPresence } from './AnimatedPresence';

interface ConfirmDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog should close. */
  onClose: () => void;
  /** Called when the user confirms the action. */
  onConfirm: () => void;
  /** Dialog title. */
  title: string;
  /** Description text (supports React nodes for emphasis). */
  description: React.ReactNode;
  /** Label for the confirm button. */
  confirmLabel?: string;
  /** Whether the action is currently in progress. */
  loading?: boolean;
  /** Visual variant: 'danger' (red) or 'warning' (orange). */
  variant?: 'danger' | 'warning';
  /** Optional error message shown inside the dialog after a failed action. */
  error?: string | null;
}

const variantStyles = {
  danger: {
    icon: 'bg-danger/20',
    iconColor: 'text-danger',
    button: 'bg-danger hover:bg-danger',
    buttonText: 'text-danger-foreground',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  },
  warning: {
    icon: 'bg-warning/20',
    iconColor: 'text-warning',
    button: 'bg-warning hover:bg-warning',
    buttonText: 'text-warning-foreground',
    iconPath: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636',
  },
};

/**
 * Confirmation dialog with danger/warning variants.
 * Built on Modal with a consistent layout: icon, title, description, confirm/cancel buttons.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  loading = false,
  variant = 'danger',
  error,
}) => {
  const styles = variantStyles[variant];

  return (
    <Modal open={open} onClose={onClose}>
      <div className="text-center">
        <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${styles.icon}`}>
          <svg className={`w-8 h-8 ${styles.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={styles.iconPath} />
          </svg>
        </div>

        <div className="mt-6">
          <DialogTitle as="h3" className="text-xl font-bold text-foreground mb-2">
            {title}
          </DialogTitle>
          {/* div, not p: description accepts arbitrary ReactNode content
              (lists, tags) which must not nest inside a paragraph. */}
          <div className="text-muted-foreground">{description}</div>
        </div>

        <AnimatedPresence show={Boolean(error)}>
          <p role="alert" className="pt-6 text-xs text-danger">{error}</p>
        </AnimatedPresence>

        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={onConfirm}
            aria-busy={loading}
            disabled={loading}
            className={`block w-full font-bold py-3 px-6 rounded-lg transition-colors ${styles.button} ${styles.buttonText} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <ButtonContent loading={loading}>{confirmLabel}</ButtonContent>
          </button>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="block w-full bg-surface-raised hover:bg-surface-hover text-foreground font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};
